import {makeAutoObservable, reaction, runInAction} from 'mobx';
import { formatDateInTimeZone, formatTimeInTimeZone } from '../../utils/timezone';
import { Service } from '../../model/professional/service-model';
import { Option } from '../../model/professional/option-model';
import { OptionCategory } from '../../model/professional/option-category-model';
import { BookingListItemPublic } from '../../model/professional/booking-model';
import { BookingCreateParams } from '../../model/client/client-booking-model';
import toastStore from '../../store/toast-store';
import {ClientAvailabilityProfessionalModel} from "../../model/client/client-availability-professional-model";
import {ClientBookingModel} from "../../model/client/client-booking-model";
import {ClientServiceModel} from "../../model/client/client-service-model";
import ClientReviewModel from "../../model/client/client-review-model";
import ClientPaymentModel from "../../model/client/client-payment-model";
import ClientPromoCodeModel from "../../model/client/client-promo-code-model";

class ClientBookingViewModel {
  // Données du professionnel
  professionalId: string | undefined = undefined;
  businessName: string = '';
  reservationConditions: string = '';
  city: string = '';
  description: string = '';
  logoUrl: string = '';
  galleryPhotos: string[] = [];
  reviews: any[] = [];
  
  // Données des services et options
  services: Service[] = [];
  selectedService: Service | null = null;
  selectedCollaborator: any = null; // Collaborateur sélectionné pour le service
  optionCategories: OptionCategory[] = [];
  selectedOptions: Option[] = [];

  // Multi-services: panier de services
  selectedServices: Array<{
    service: Service;
    options: Option[];
    collaborator: any;
  }> = [];

  // Données de réservation
  selectedDate: Date | null = null;
  selectedSlot: any = null;
  availableSlots: any[] = [];
  availableDates: string[] = [];
  // Informations client
  clientFirstName: string = '';
  clientLastName: string = '';
  clientEmail: string = '';
  clientPhone: string = '';

  // Code promo
  promoCode: string = '';
  isValidatingPromoCode: boolean = false;
  isPromoCodeValid: boolean | null = null;
  promoCodeDiscountAmount: number = 0;
  promoCodeValidationMessage: string = '';
  validatedPromoCodeId: string | null = null;
  promoCodeEligibleServiceIds: string[] = []; // IDs des services éligibles au code promo
  promoCodeEligibleAmount: number = 0; // Montant total des services éligibles

  // États de l'interface
  isLoading: boolean = false;
  error: string | null = null;
  isDrawerOpen: boolean = false;

  // Paiement en ligne
  onlinePaymentRequired: boolean = false;
  stripeConnected: boolean = false;

  // Propriété calculée pour déterminer si le service sélectionné nécessite une réservation
  get requiresBooking(): boolean {
    // Si on a plusieurs services sélectionnés, vérifier si au moins un nécessite une réservation
    if (this.selectedServices.length > 0) {
      return this.selectedServices.some(item => item.service.requiresBooking);
    }

    // Sinon, vérifier le service actuellement sélectionné (mode ajout)
    return this.selectedService?.requiresBooking ?? true; // Par défaut, on considère qu'une réservation est nécessaire
  }

  // Propriété calculée pour calculer la durée totale (service + options)
  get totalDuration(): number {
    // Si on a plusieurs services, calculer la durée totale de tous les services
    if (this.selectedServices.length > 0) {
      return this.selectedServices.reduce((total, item) => {
        const serviceDuration = item.service.duration || 0;
        const optionsDuration = item.options.reduce((optTotal, option) => {
          return optTotal + (option.durationMinutes || 0);
        }, 0);
        return total + serviceDuration + optionsDuration;
      }, 0);
    }

    // Sinon, calculer pour le service actuellement sélectionné (mode ajout)
    if (!this.selectedService) return 0;

    const serviceDuration = this.selectedService.duration || 0;
    const optionsDuration = this.selectedOptions.reduce((total, option) => {
      return total + (option.durationMinutes || 0);
    }, 0);

    return serviceDuration + optionsDuration;
  }

  // Propriété calculée pour vérifier si la réservation peut être confirmée
  get canConfirmBooking(): boolean {
    // Si le paiement en ligne est requis mais que Stripe n'est pas configuré, on ne peut pas confirmer
    if (this.onlinePaymentRequired && !this.stripeConnected) {
      return false;
    }
    return true;
  }

  // Message d'erreur si la réservation ne peut pas être confirmée
  get bookingBlockedMessage(): string | null {
    if (this.onlinePaymentRequired && !this.stripeConnected) {
      return 'Le professionnel n\'a pas encore configuré son compte de paiement. Les réservations en ligne ne sont pas disponibles pour le moment. Veuillez contacter directement le professionnel.';
    }
    return null;
  }

  protected bookingModel: ClientBookingModel
  protected clientServiceModel: ClientServiceModel
  protected clientAvailabilityProfessionalModel: ClientAvailabilityProfessionalModel
  protected clientReviewModel = ClientReviewModel
  protected clientPaymentModel: ClientPaymentModel
  protected clientPromoCodeModel: ClientPromoCodeModel

  constructor() {
    makeAutoObservable(this);
    this.bookingModel = ClientBookingModel.getInstance()
    this.clientServiceModel = ClientServiceModel.getInstance()
    this.clientAvailabilityProfessionalModel = ClientAvailabilityProfessionalModel.getInstance()
    this.clientPaymentModel = ClientPaymentModel.getInstance()
    this.clientPromoCodeModel = ClientPromoCodeModel.getInstance()

    reaction(
        () => this.professionalId,
        () => {
          this.loadProfessionalReviews().then();
        }
    );

    // Recharger les créneaux disponibles et l'aperçu quand les options sélectionnées changent
    reaction(
        () => `${this.selectedOptions.length}:${this.selectedOptions.map(opt => opt.id).sort().join(',')}`,
        () => {
          // Recharger l'aperçu des disponibilités car les options affectent la durée totale
          if (this.selectedCollaborator && this.selectedService) {
            this.loadAvailabilityOverview();
          }

          // Recharger uniquement si une date est déjà sélectionnée
          if (this.selectedDate) {
            this.loadAvailableSlots(this.selectedDate);
          }
        }
    );
    // ClientReviewModel est déjà une instance singleton
  }

  /**
   * Définit l'ID du professionnel
   */
  setProfessionalId = (id: string | undefined) => {
    this.professionalId = id;
  };

  /**
   * Définit le nom de l'entreprise
   */
  setBusinessName = (name: string) => {
    this.businessName = name;
  };

  /**
   * Définit les conditions de réservation
   */
  setReservationConditions = (conditions: string) => {
    this.reservationConditions = conditions;
  };
  
  /**
   * Définit la ville du professionnel
   */
  setCity = (city: string) => {
    this.city = city;
  };
  
  /**
   * Définit la description du professionnel
   */
  setDescription = (description: string) => {
    this.description = description;
  };
  
  /**
   * Définit l'URL du logo (photo de profil) du professionnel
   */
  setLogoUrl = (logoUrl: string) => {
    this.logoUrl = logoUrl;
  };

  setGalleryPhotos = (photos: string[]) => {
    this.galleryPhotos = photos;
  };
  
  /**
   * Définit les avis du professionnel
   */
  setReviews = (reviews: any[]) => {
    this.reviews = reviews;
  };

  /**
   * Définit la liste des services
   */
  setServices = (services: Service[]) => {
    this.services = services;
  };

  /**
   * Définit le service sélectionné
   */
  setSelectedService = (service: Service | null) => {
    this.selectedService = service;
  };

  /**
   * Définit le collaborateur sélectionné
   */
  setSelectedCollaborator = (collaborator: any) => {
    this.selectedCollaborator = collaborator;
  };

  /**
   * Définit les catégories d'options
   */
  setOptionCategories = (categories: OptionCategory[]) => {
    this.optionCategories = categories;
  };

  /**
   * Définit les options sélectionnées
   */
  setSelectedOptions = (options: Option[]) => {
    this.selectedOptions = options;
  };

  /**
   * Définit la date sélectionnée
   */
  setSelectedDate = (date: Date | null) => {
    this.selectedDate = date;
  };

  /**
   * Définit le créneau sélectionné
   */
  setSelectedSlot = (slot: any) => {
    this.selectedSlot = slot;
  };

  /**
   * Définit les créneaux disponibles
   */
  setAvailableSlots = (slots: any[]) => {
    this.availableSlots = slots;
  };

  /**
   * Définit les dates disponibles
   */
  setAvailableDates = (dates: string[]) => {
    this.availableDates = dates;
  };

  /**
   * Définit le prénom du client
   */
  setClientFirstName = (firstName: string) => {
    this.clientFirstName = firstName;
  };

  /**
   * Définit le nom du client
   */
  setClientLastName = (lastName: string) => {
    this.clientLastName = lastName;
  };

  /**
   * Définit l'email du client
   */
  setClientEmail = (email: string) => {
    this.clientEmail = email;
  };

  /**
   * Définit le téléphone du client
   */
  setClientPhone = (phone: string) => {
    this.clientPhone = phone;
  };

  /**
   * Définit l'état de chargement
   */
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  /**
   * Définit le message d'erreur
   */
  setError = (error: string | null) => {
    this.error = error;
  };

  /**
   * Définit l'état d'ouverture du drawer
   */
  setIsDrawerOpen = (isOpen: boolean) => {
    this.isDrawerOpen = isOpen;
  };
  
  // Initialisation avec l'ID du professionnel
  async initialize(businessName: string) {
    this.setIsLoading(true);
    this.setError(null);

    // Vérifier si le paiement en ligne est requis
    await this.checkOnlinePaymentRequired(businessName);

    // Charger les services et les avis du professionnel
    await this.loadServices(businessName);
  }

  /**
   * Vérifie si le paiement en ligne est requis pour cette entreprise
   */
  async checkOnlinePaymentRequired(businessName: string) {
    try {
      const result = await this.bookingModel.checkOnlinePaymentRequired(businessName);
      runInAction(() => {
        if (result.success && result.data) {
          this.onlinePaymentRequired = result.data.onlinePaymentRequired;
          this.stripeConnected = result.data.stripeConnected || false;
        }
      });
    } catch (error) {
      console.error('Error checking payment requirement:', error);
      // En cas d'erreur, on suppose que le paiement n'est pas requis par défaut
      runInAction(() => {
        this.onlinePaymentRequired = false;
        this.stripeConnected = false;
      });
    }
  }

  // Chargement des services du professionnel
  async loadServices(businessName: string | undefined) {
    if (!businessName) return;
    
    try {
      const servicesResponse = await this.clientServiceModel.getServicesByProfessional(businessName);
      
      // Filtrer les services actifs uniquement
      // Le backend envoie déjà les collaborateurs dans le bon format
      const filteredServices = Array.isArray(servicesResponse.data)
        ? servicesResponse.data.filter((service: any) => service.isActive !== false)
        : [];

      // Extraire le companyId du premier service pour récupérer les avis
      if (servicesResponse.data && servicesResponse.data.length > 0) {
        const companyId = servicesResponse.data[0].companyId;
        this.setProfessionalId(companyId);
      }

      this.setServices(filteredServices);
      
      // Extraire les informations de la company via la relation company
      if (servicesResponse.data && servicesResponse.data.length > 0 && (servicesResponse.data[0] as any).company) {
        const company = (servicesResponse.data[0] as any).company;

        if (company.businessName) {
          this.setBusinessName(company.businessName);
        }
        if (company.reservationConditions) {
          this.setReservationConditions(company.reservationConditions);
        }
        if (company.city) {
          this.setCity(company.city);
        }
        if (company.description) {
          this.setDescription(company.description);
        }
        if (company.companyLogo) {
          this.setLogoUrl(company.companyLogo);
        }
        if (company.galleryPhoto && Array.isArray(company.galleryPhoto)) {
          this.setGalleryPhotos(company.galleryPhoto);
        }

      }
      
      this.setIsLoading(false);
    } catch (error) {
      this.setError('Erreur lors du chargement des services');
      this.setIsLoading(false);
    }
  }
  
  // Sélection d'un service
  async selectService(service: Service) {
    this.setSelectedService(service);
    this.setSelectedOptions([]);
    this.setSelectedCollaborator(null);
    this.setIsDrawerOpen(true);

    // Charger les catégories d'options pour ce service d'abord
    await this.loadOptionCategories(service.id);

    // Sélection automatique du collaborateur s'il n'y en a qu'un seul
    // Mais seulement après que les options soient chargées
    if (service.collaborators && service.collaborators.length === 1) {
      this.selectCollaborator(service.collaborators[0]);
    }
  }

  // Sélection d'un collaborateur
  selectCollaborator(collaborator: any) {
    this.setSelectedCollaborator(collaborator);
    // Réinitialiser la date et les créneaux car ils dépendent du collaborateur
    this.setSelectedDate(null);
    this.setSelectedSlot(null);
    this.setAvailableSlots([]);

    // Ajouter automatiquement au panier si le service n'a pas d'options
    if (this.optionCategories.length === 0) {
      this.addServiceToCart();
    }
    // Sinon, l'utilisateur devra cliquer sur le bouton "Ajouter au panier"
  }

  /**
   * Ajoute le service actuellement configuré au panier
   */
  addServiceToCart() {
    if (!this.selectedService || !this.selectedCollaborator) {
      toastStore.error('Veuillez sélectionner un service et un collaborateur');
      return false;
    }

    // Vérifier que toutes les options obligatoires sont sélectionnées
    const requiredCategories = this.optionCategories.filter(cat => cat.isRequired);
    const missingRequiredOptions = requiredCategories.some(cat =>
      !this.selectedOptions.some(opt => opt.categoryId === cat.id)
    );

    if (missingRequiredOptions) {
      toastStore.error('Veuillez sélectionner toutes les options obligatoires');
      return false;
    }

    // Ajouter au panier
    this.selectedServices.push({
      service: { ...this.selectedService },
      options: [...this.selectedOptions],
      collaborator: { ...this.selectedCollaborator }
    });

    // Réinitialiser la sélection actuelle pour permettre l'ajout d'un autre service
    this.setSelectedService(null);
    this.setSelectedOptions([]);
    this.setSelectedCollaborator(null);
    this.setOptionCategories([]);

    // Recharger les créneaux disponibles avec la nouvelle durée totale
    // On utilise le collaborateur du premier service du panier
    if (this.selectedServices.length > 0 && this.selectedServices[0].collaborator) {
      this.loadAvailabilityOverview();

      // Si une date était déjà sélectionnée, recharger les créneaux pour cette date
      if (this.selectedDate) {
        this.loadAvailableSlots(this.selectedDate);
      }
    }

    toastStore.success('Service ajouté au panier');
    return true;
  }

  /**
   * Retire un service du panier
   */
  removeServiceFromCart(index: number) {
    if (index >= 0 && index < this.selectedServices.length) {
      this.selectedServices.splice(index, 1);

      // Recharger les créneaux avec la nouvelle durée totale
      if (this.selectedServices.length > 0 && this.selectedServices[0].collaborator) {
        this.loadAvailabilityOverview();

        // Si une date était déjà sélectionnée, recharger les créneaux pour cette date
        if (this.selectedDate) {
          this.loadAvailableSlots(this.selectedDate);
        }
      }

      // Si le panier devient vide, fermer le drawer
      if (this.selectedServices.length === 0) {
        this.setIsDrawerOpen(false);
        this.setSelectedDate(null);
        this.setSelectedSlot(null);
        this.setAvailableSlots([]);
        this.setAvailableDates([]);
      }
    }
  }

  /**
   * Vide complètement le panier
   */
  clearCart() {
    this.selectedServices = [];
  }

  /**
   * Calcule le prix d'un service avec ses options
   */
  private calculateServicePrice(service: Service, options: Option[]): number {
    const servicePrice = typeof service.price === 'number' ?
      service.price :
      parseFloat(service.price as any) || 0;

    const optionsPrice = options.reduce((total, option) => {
      const optionPrice = typeof option.price === 'number' ?
        option.price :
        parseFloat(option.price as any) || 0;
      return total + optionPrice;
    }, 0);

    return Math.round((servicePrice + optionsPrice) * 100) / 100;
  }

  /**
   * Calcule la durée totale d'un service avec ses options
   */
  private calculateServiceDuration(service: Service, options: Option[]): number {
    const serviceDuration = service.duration || 0;
    const optionsDuration = options.reduce((total, option) => {
      return total + (option.durationMinutes || 0);
    }, 0);

    return serviceDuration + optionsDuration;
  }
  
  // Chargement des catégories d'options pour un service
  async loadOptionCategories(serviceId: string) {
    try {
      this.setIsLoading(true);
      
      // Si le service a des catégories d'options, les utiliser directement
      if (this.selectedService && this.selectedService.optionCategories) {
        // Filtrer les catégories d'options actives et leurs options actives
        const filteredCategories = Array.isArray(this.selectedService!.optionCategories) 
          ? this.selectedService!.optionCategories
            .filter((category: any) => category.isActive !== false)
            .map((category: any) => ({
              ...category,
              // Filtrer pour n'inclure que les options actives dans chaque catégorie
              options: category.options ? 
                category.options.filter((option: any) => option.isActive !== false) : 
                []
            }))
          : [];
        
        this.setOptionCategories(filteredCategories);
        this.setIsLoading(false);
        return;
      }
      
      // Sinon, les charger via le modèle client
      const categories = await this.clientServiceModel.getOptionCategoriesByService(serviceId);
      
      // Filtrer les catégories d'options actives et leurs options actives
      const filteredCategories = Array.isArray(categories) 
        ? categories
          .filter((category: any) => category.isActive !== false)
          .map((category: any) => ({
            ...category,
            // Filtrer pour n'inclure que les options actives dans chaque catégorie
            options: category.options ? 
              category.options.filter((option: any) => option.isActive !== false) : 
              []
          }))
        : [];
      
      this.setOptionCategories(filteredCategories);
      this.setIsLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des options:', error);
      this.setIsLoading(false);
    }
  }
  
  // Sélection d'une option
  selectOption(option: Option, isSelected: boolean) {
      if (isSelected) {
        // Vérifier si l'option appartient à une catégorie à choix unique
        const category = this.optionCategories.find(cat => cat.id === option.categoryId);
        
        if (category && !category.isMultipleChoice) {
          // Si c'est une catégorie à choix unique, désélectionner les autres options de cette catégorie
          this.setSelectedOptions(this.selectedOptions.filter(opt => opt.categoryId !== option.categoryId));
        }
        
        // Ajouter l'option sélectionnée
        this.setSelectedOptions([...this.selectedOptions, option]);
      } else {
        // Retirer l'option désélectionnée
        this.setSelectedOptions(this.selectedOptions.filter(opt => opt.id !== option.id));
      }
  }
  
  // Mise à jour des informations client
  updateClientInfo(field: 'firstName' | 'lastName' | 'email' | 'phone', value: string) {
    switch (field) {
      case 'firstName':
        this.setClientFirstName(value);
        break;
      case 'lastName':
        this.setClientLastName(value);
        break;
      case 'email':
        this.setClientEmail(value);
        break;
      case 'phone':
        this.setClientPhone(value);
        break;
    }
  }

  // Gestion du code promo
  setPromoCode(code: string) {
    this.promoCode = code;
  }

  setIsValidatingPromoCode(value: boolean) {
    this.isValidatingPromoCode = value;
  }

  setPromoCodeValidation(
    isValid: boolean | null,
    discountAmount: number,
    message: string,
    promoCodeId: string | null = null,
    eligibleServiceIds: string[] = [],
    eligibleAmount: number = 0
  ) {
    this.isPromoCodeValid = isValid;
    this.promoCodeDiscountAmount = discountAmount;
    this.promoCodeValidationMessage = message;
    this.validatedPromoCodeId = promoCodeId;
    this.promoCodeEligibleServiceIds = eligibleServiceIds;
    this.promoCodeEligibleAmount = eligibleAmount;
  }

  async validatePromoCode() {
    // Vérifier qu'on a au moins un service (soit dans le panier, soit sélectionné)
    const hasServices = this.selectedServices.length > 0 || this.selectedService !== null;
    if (!this.promoCode || !this.professionalId || !hasServices) {
      return;
    }

    this.setIsValidatingPromoCode(true);

    try {
      const servicesToValidate: Array<{ serviceId: string; amount: number }> = [];

      if (this.selectedServices.length > 0) {
        this.selectedServices.forEach(item => {
          const servicePrice = this.calculateServicePrice(item.service, item.options);
          servicesToValidate.push({
            serviceId: item.service.id,
            amount: servicePrice
          });
        });
      } else if (this.selectedService) {
        // Service en cours de configuration
        const servicePrice = this.calculateServicePrice(this.selectedService, this.selectedOptions);
        servicesToValidate.push({
          serviceId: this.selectedService.id,
          amount: servicePrice
        });
      }

      // Valider le code promo pour chaque service et collecter les éligibles
      const eligibleServiceIds: string[] = [];
      let eligibleAmount = 0;
      let promoCodeId: string | null = null;
      let discountPercentage = 0;

      for (const serviceData of servicesToValidate) {
        const result = await this.clientPromoCodeModel.validatePromoCode({
          code: this.promoCode,
          clientId: 'guest',
          companyId: this.professionalId,
          amount: serviceData.amount,
          serviceId: serviceData.serviceId,
        });

        if (result.success && result.data?.isValid) {
          eligibleServiceIds.push(serviceData.serviceId);
          eligibleAmount += serviceData.amount;
          promoCodeId = result.data.promoCodeId || null;
          // Calculer le pourcentage de réduction à partir du premier service éligible
          if (discountPercentage === 0 && serviceData.amount > 0) {
            discountPercentage = (result.data.discountAmount / serviceData.amount) * 100;
          }
        }
      }

      if (eligibleServiceIds.length > 0) {
        // Calculer la réduction totale sur le montant éligible
        const totalDiscount = Math.round((eligibleAmount * discountPercentage / 100) * 100) / 100;
        const eligibleCount = eligibleServiceIds.length;
        const totalCount = servicesToValidate.length;

        let message = `Code promo appliqué sur ${eligibleCount}/${totalCount} service(s) (-${discountPercentage}%)`;

        this.setPromoCodeValidation(
          true,
          totalDiscount,
          message,
          promoCodeId,
          eligibleServiceIds,
          eligibleAmount
        );
        toastStore.success(message);
      } else {
        this.setPromoCodeValidation(
          false,
          0,
          'Code promo non applicable aux services sélectionnés',
          null,
          [],
          0
        );
        toastStore.error('Code promo non applicable aux services sélectionnés');
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      this.setPromoCodeValidation(
        false,
        0,
        'Erreur lors de la validation du code promo',
        null,
        [],
        0
      );
      toastStore.error('Erreur lors de la validation du code promo');
    } finally {
      this.setIsValidatingPromoCode(false);
    }
  }

  removePromoCode() {
    this.setPromoCode('');
    this.setPromoCodeValidation(null, 0, '', null, [], 0);
  }
  
  // Sélection d'une date
  selectDate(date: Date) {
    this.setSelectedDate(date);
    this.setSelectedSlot(null);
    
    // Charger les créneaux disponibles pour cette date
    this.loadAvailableSlots(date);
  }
  
  // Chargement des créneaux disponibles pour une date
  async loadAvailableSlots(date: Date) {
    // Utiliser le panier si disponible, sinon le service en cours de configuration
    const collaborator = this.selectedServices.length > 0
      ? this.selectedServices[0].collaborator
      : this.selectedCollaborator;

    if (!collaborator) {
      console.warn('Collaborateur non disponible');
      return;
    }

    try {
      this.setIsLoading(true);
      this.setSelectedDate(date);

      const formattedDate = date.toISOString().split('T')[0];

      // Utiliser le collaboratorCompagnieId (id du collaborator_compagnies)
      const collaboratorCompagnieId = collaborator.collaboratorCompagnieId;

      // Si on a des services dans le panier, utiliser le premier service et la durée totale
      // Sinon, utiliser le service en cours de configuration
      const serviceId = this.selectedServices.length > 0
        ? this.selectedServices[0].service.id
        : this.selectedService?.id;

      const totalDuration = this.totalDuration || 60;

      if (!serviceId) {
        console.warn('Service ID non disponible');
        this.setIsLoading(false);
        return;
      }

      // Pour les multi-services, on ne passe pas d'optionIds (la durée totale est déjà calculée)
      const selectedOptionIds = this.selectedServices.length > 0
        ? []
        : this.selectedOptions.map(option => option.id);

      // Utiliser availabilityViewModel pour récupérer les créneaux disponibles
      const response = await ClientAvailabilityProfessionalModel.getInstance().getProfessionalAvailability(
        collaboratorCompagnieId,
        serviceId,
        totalDuration,
        formattedDate,
        undefined, // bufferTime
        selectedOptionIds
      );

      if (response.success && response.data && response.data.length > 0) {
        this.setAvailableSlots(response.data);
      } else {
        this.setAvailableSlots([]);
      }

      this.setIsLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des créneaux:', error);
      this.setError('Erreur lors du chargement des créneaux');
      this.setIsLoading(false);
    }
  }

  // Chargement de l'aperçu des disponibilités sur une période (pour afficher les dates disponibles dans le calendrier)
  async loadAvailabilityOverview(monthsAhead: number = 2) {
    // Utiliser le panier si disponible, sinon le service en cours de configuration
    const collaborator = this.selectedServices.length > 0
      ? this.selectedServices[0].collaborator
      : this.selectedCollaborator;

    const serviceId = this.selectedServices.length > 0
      ? this.selectedServices[0].service.id
      : this.selectedService?.id;

    if (!collaborator || !serviceId) {
      return;
    }

    try {
      const today = new Date();
      const startDate = formatDateInTimeZone(today, 'yyyy-MM-dd');

      // Calculer la date de fin (aujourd'hui + X mois)
      const endDate = new Date(today);
      endDate.setMonth(endDate.getMonth() + monthsAhead);
      const formattedEndDate = formatDateInTimeZone(endDate, 'yyyy-MM-dd');

      // Utiliser le collaboratorCompagnieId
      const collaboratorCompagnieId = collaborator.collaboratorCompagnieId;

      const totalDuration = this.totalDuration || 60;

      // Pour les multi-services, on ne passe pas d'optionIds (la durée totale est déjà calculée)
      const selectedOptionIds = this.selectedServices.length > 0
        ? []
        : this.selectedOptions.map(option => option.id);

      // Appeler l'API pour obtenir l'aperçu des disponibilités
      const response = await this.clientAvailabilityProfessionalModel.getProfessionalAvailabilityOverview(
        collaboratorCompagnieId,
        serviceId,
        totalDuration,
        startDate,
        formattedEndDate,
        selectedOptionIds
      );

      if (response.success && response.data) {
        // Extraire uniquement les dates qui ont des disponibilités
        const datesWithAvailability = response.data
          .filter(day => day.hasAvailability)
          .map(day => day.date);

        runInAction(() => {
          this.setAvailableDates(datesWithAvailability);
        });
      } else {
        runInAction(() => {
          this.setAvailableDates([]);
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'aperçu des disponibilités:', error);
      runInAction(() => {
        this.setAvailableDates([]);
      });
    }
  }

  // Sélection d'un créneau horaire
  selectTimeSlot(slot: any) {
    this.setSelectedSlot(slot);
  }

  // Ouverture/fermeture du drawer
  toggleDrawer(isOpen?: boolean) {
    const newIsOpen = isOpen !== undefined ? isOpen : !this.isDrawerOpen;
    this.setIsDrawerOpen(newIsOpen);
    
    // Si on ferme le drawer, on réinitialise le formulaire
    if (!newIsOpen) {
      this.reset();
    }
  }
  
  // Vérification de la validité du formulaire
  get isFormValid() {
    // Vérifier qu'au moins un service est dans le panier ou qu'un service est sélectionné
    const hasServices = this.selectedServices.length > 0 || this.selectedService !== null;
    if (!hasServices) return false;

    // Si un service est en cours de configuration (pas encore dans le panier),
    // vérifier que toutes les options obligatoires sont sélectionnées
    if (this.selectedService) {
      const requiredCategories = this.optionCategories.filter(cat => cat.isRequired);
      const missingRequiredOptions = requiredCategories.some(cat =>
        !this.selectedOptions.some(opt => opt.categoryId === cat.id)
      );

      if (missingRequiredOptions) return false;
    }

    // Vérifier qu'un créneau est sélectionné (uniquement si le service nécessite une réservation)
    if (this.requiresBooking && !this.selectedSlot) return false;

    // Vérifier que les informations client sont complètes
    if (!this.clientFirstName || !this.clientLastName || !this.clientEmail || !this.clientPhone) return false;

    return true;
  }
  
  // Calcul du prix total avant réduction
  get totalPriceBeforeDiscount(): number {
    // Si on a plusieurs services dans le panier, calculer leur total
    if (this.selectedServices.length > 0) {
      let total = 0;

      this.selectedServices.forEach(item => {
        // Prix du service
        const servicePrice = typeof item.service.price === 'number' ?
          item.service.price :
          parseFloat(item.service.price as any) || 0;
        total += servicePrice;

        // Prix des options du service
        item.options.forEach(option => {
          const optionPrice = typeof option.price === 'number' ?
            option.price :
            parseFloat(option.price as any) || 0;
          total += optionPrice;
        });
      });

      return Math.round(total * 100) / 100;
    }

    // Sinon, calculer pour le service en cours de configuration (mode ajout)
    if (!this.selectedService) return 0;

    // S'assurer que le prix du service est un nombre
    let total = typeof this.selectedService.price === 'number' ?
      this.selectedService.price :
      parseFloat(this.selectedService.price as any) || 0;

    // Ajouter le prix des options sélectionnées
    this.selectedOptions.forEach(option => {
      // S'assurer que le prix de l'option est un nombre
      const optionPrice = typeof option.price === 'number' ?
        option.price :
        parseFloat(option.price as any) || 0;

      total += optionPrice;
    });

    // Arrondir à 2 décimales pour éviter les erreurs de calcul flottant
    return Math.round(total * 100) / 100;
  }

  // Calcul du prix total après réduction
  get totalPrice(): number {
    const basePrice = this.totalPriceBeforeDiscount;

    // Appliquer la réduction si un code promo valide est présent
    if (this.isPromoCodeValid && this.promoCodeDiscountAmount > 0) {
      const discountedPrice = basePrice - this.promoCodeDiscountAmount;
      return Math.max(0, Math.round(discountedPrice * 100) / 100);
    }

    return basePrice;
  }

  /**
   * Crée une nouvelle réservation et redirige vers le paiement Stripe
   * @returns ID de la réservation créée ou false en cas d'erreur
   */
  public async createBooking(): Promise<string | boolean> {
    // Vérification: soit on a des services dans le panier, soit un service en cours de configuration
    const hasServicesInCart = this.selectedServices.length > 0;
    const hasCurrentService = this.selectedService !== null;

    if (!hasServicesInCart && !hasCurrentService) {
      toastStore.error('Veuillez sélectionner au moins un service');
      return false;
    }

    // Si on a un service en cours de configuration, on le valide
    if (hasCurrentService && !this.selectedCollaborator) {
      toastStore.error('Veuillez sélectionner un professionnel');
      return false;
    }

    // Vérifier si le service nécessite une réservation (on prend le premier service du panier ou le service actuel)
    if (this.requiresBooking && !this.selectedSlot) {
      toastStore.error('Veuillez sélectionner un créneau horaire');
      return false;
    }

    if (!this.clientFirstName || !this.clientLastName || !this.clientEmail || !this.clientPhone) {
      toastStore.error('Veuillez remplir toutes les informations client');
      return false;
    }

    try {
      this.setIsLoading(true);

      // Build services array from cart or current selection
      let services: Array<{
        serviceId: string;
        optionIds: string[];
        price: number;
        duration: number;
      }>;

      if (hasServicesInCart) {
        // Utiliser les services du panier
        services = this.selectedServices.map((item) => ({
          serviceId: item.service.id,
          optionIds: item.options.map(opt => opt.id),
          price: this.calculateServicePrice(item.service, item.options),
          duration: this.calculateServiceDuration(item.service, item.options)
        }));
      } else {
        // Service unique en cours de configuration
        services = [{
          serviceId: this.selectedService!.id,
          optionIds: this.selectedOptions.map(option => option.id),
          price: this.totalPriceBeforeDiscount,
          duration: this.totalDuration
        }];
      }

      // Le collaboratorCompagnieId est celui du premier service (on suppose que tous les services sont du même professionnel)
      const collaboratorCompagnieId = hasServicesInCart
        ? this.selectedServices[0].collaborator.collaboratorCompagnieId
        : this.selectedCollaborator.collaboratorCompagnieId;

      let bookingData: BookingCreateParams = {
        services: services,
        collaboratorCompagnieId: collaboratorCompagnieId,
        firstName: this.clientFirstName,
        lastName: this.clientLastName,
        email: this.clientEmail,
        phone: this.clientPhone,
        notes: ''
      };

      console.log('Booking data to send:', bookingData);
      console.log('Selected collaborator:', hasServicesInCart ? this.selectedServices[0].collaborator : this.selectedCollaborator);

      if (this.requiresBooking && this.selectedSlot && this.selectedDate) {
        const formattedDate = this.selectedDate.toISOString().split('T')[0];

        const localStartDateTime = `${formattedDate}T${this.selectedSlot.startTime}:00`;
        const localEndDateTime = `${formattedDate}T${this.selectedSlot.endTime}:00`;

        const startDate = new Date(localStartDateTime);
        const endDate = new Date(localEndDateTime);

        const startDateTime = startDate.toISOString();
        const endDateTime = endDate.toISOString();

        bookingData = {
          ...bookingData,
          timeSlotId: this.selectedSlot.configuredSlotId,
          startTime: startDateTime,
          endTime: endDateTime,
        };
      } else {
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];

        const startDate = new Date(`${formattedDate}T00:00:00`);
        const endDate = new Date(`${formattedDate}T00:00:00`);

        bookingData = {
          ...bookingData,
          startTime: startDate.toISOString(),
          endTime: endDate.toISOString(),
        };
      }

      // Vérifier si le paiement en ligne est requis
      if (this.onlinePaymentRequired) {
        // CAS 1 : AVEC PAIEMENT (Plan Pro/Multi)
        // Préparer les paramètres pour la session de paiement
        // Les données de réservation seront stockées dans Stripe et la réservation
        // sera créée APRÈS confirmation du paiement via webhook
        const paymentParams: any = {
          bookingData: {
            timeSlotId: bookingData.timeSlotId || null,
            services: bookingData.services,
            firstName: bookingData.firstName,
            lastName: bookingData.lastName,
            email: bookingData.email,
            phone: bookingData.phone,
            collaboratorCompagnieId: bookingData.collaboratorCompagnieId,
            startTime: bookingData.startTime,
            endTime: bookingData.endTime,
            note: bookingData.notes || null,
            // Envoyer le prix AVANT réduction, le serveur appliquera la réduction avec discountAmount
            totalPrice: this.totalPriceBeforeDiscount,
          },
        };

        // Ajouter les informations du code promo si valide
        if (this.isPromoCodeValid && this.validatedPromoCodeId) {
          paymentParams.promoCodeId = this.validatedPromoCodeId;
          paymentParams.discountAmount = this.promoCodeDiscountAmount;
        }

        const paymentResult = await this.clientPaymentModel.createCheckoutSession(paymentParams);

        if (paymentResult.success && paymentResult.data?.url) {
          // Redirection vers Stripe Checkout
          // La réservation sera créée après paiement réussi
          window.location.href = paymentResult.data.url;
          return true;
        } else {
          toastStore.error(paymentResult.message || 'Erreur lors de la création de la session de paiement');
          return false;
        }
      } else {
        // SANS PAIEMENT (Plan Junior)
        // Créer la réservation directement sans passer par Stripe
        const bookingResult = await this.bookingModel.createBooking(bookingData);

        if (bookingResult.success && bookingResult.data) {
          const booking = Array.isArray(bookingResult.data) ? bookingResult.data[0] : bookingResult.data;

          if (booking && booking.id) {
            const currentLang = window.location.pathname.split('/')[1] || 'fr';
            const confirmationUrl = `/${currentLang}/payment/success?booking_id=${booking.id}&business_name=${encodeURIComponent(this.businessName)}`;
            window.location.href = confirmationUrl;
            return true;
          }
        }

        toastStore.error(bookingResult.message || 'Erreur lors de la création de la réservation');
        return false;
      }
    } catch (error) {
      this.setError("'Erreur lors de la création de la réservation'");
      toastStore.error("'Erreur lors de la création de la réservation'");
      return false;
    } finally {
      this.setIsLoading(false);
    }
  }

  /**
   * Récupère une réservation par son ID
   * @param bookingId ID de la réservation à récupérer
   * @returns Réservation formatée ou tableau vide en cas d'erreur
   */
  public async getBookings(bookingId: string): Promise<BookingListItemPublic[]> {
    try {
      if (!bookingId) {
        console.warn('Aucun ID de réservation fourni');
        return [];
      }

      const response = await this.bookingModel.getBookingById(bookingId);
      if (response.success && response.data) {
        return [this.mapToPublicBooking(response.data)];
      }
      
      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération de la réservation:', error);
      return [];
    }
  }
  
  private mapToPublicBooking(booking: any): BookingListItemPublic {
    // Support both legacy single service and new multi-services format
    const serviceName = booking.services && booking.services.length > 0
      ? booking.services.map((s: any) => s.serviceName).join(', ')
      : booking.serviceName || '';

    const options = booking.services && booking.services.length > 0
      ? booking.services.flatMap((s: any) => s.options || [])
      : booking.options || [];

    return {
      id: booking.id,
      serviceName: serviceName,
      startTime: booking.startTime,
      endTime: booking.endTime,
      status: booking.status,
      totalPrice: booking.totalPrice,
      firstName: booking.firstName,
      lastName: booking.lastName,
      businessName: booking.businessName,
      options: options.map((opt: any) => ({
        id: opt.id,
        name: opt.name,
        price: opt.price
      }))
    };
  }
  
  /**
   * Vérifie si une date est valide
   * @param dateStr Chaîne de date à vérifier
   * @returns true si la date est valide, false sinon
   */
  public isValidDate(dateStr: string | undefined): boolean {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  }
  
  /**
   * Vérifie si un service n'a pas de créneau horaire (heures à 00:00)
   * @param startTime Heure de début
   * @param endTime Heure de fin
   * @returns true si le service n'a pas de créneau, false sinon
   */
  public isNoTimeSlotService(startTime: string | undefined, endTime: string | undefined): boolean {
    if (!this.isValidDate(startTime) || !this.isValidDate(endTime)) return true;
    
    const start = new Date(startTime!);
    const end = new Date(endTime!);
    
    return start.getHours() === 0 && start.getMinutes() === 0 &&
           end.getHours() === 0 && end.getMinutes() === 0;
  }
  
  /**
   * Formate une date pour l'affichage
   * @param dateStr Chaîne de date à formater
   * @returns Date formatée ou date du jour si invalide
   */
  public formatBookingDate(dateStr: string | undefined): string {
    const date = this.isValidDate(dateStr) ? new Date(dateStr!) : new Date();
    
    return formatDateInTimeZone(date, 'EEEE d MMMM yyyy');
  }
  
  /**
   * Formate l'heure d'une réservation pour l'affichage
   * @param startTime Heure de début
   * @param endTime Heure de fin
   * @returns Heure formatée ou 'Sans créneau' si pas de créneau
   */
  public formatBookingTime(startTime: string | undefined, endTime: string | undefined): string {
    if (this.isNoTimeSlotService(startTime, endTime)) {
      return 'Sans créneau';
    }
    
    const start = new Date(startTime!);
    const end = new Date(endTime!);
    
    const startFormatted = formatTimeInTimeZone(start, 'HH:mm');
    const endFormatted = formatTimeInTimeZone(end, 'HH:mm');
    
    return `${startFormatted} - ${endFormatted}`;
  }


  /**
   * Charge les avis du professionnel
   */
  async loadProfessionalReviews() {
    if (!this.professionalId) return;
    
    try {
      const reviewsResponse = await this.clientReviewModel.getProfessionalReviews(this.professionalId);
      if (reviewsResponse.success && reviewsResponse.reviews) {
        this.setReviews(reviewsResponse.reviews);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
    }
  }
  
  /**
   * Réinitialise le formulaire
   */
  reset() {
    this.setSelectedService(null);
    this.setSelectedCollaborator(null);
    this.setSelectedOptions([]);
    this.setSelectedDate(null);
    this.setSelectedSlot(null);
    this.setAvailableSlots([]);
    this.setClientFirstName('');
    this.setClientLastName('');
    this.setClientEmail('');
    this.setClientPhone('');
    this.removePromoCode();
    this.setIsDrawerOpen(false);
    this.setError(null);
  }
}

export const bookingClientViewModel = new ClientBookingViewModel();
